import React, { useCallback, useEffect, useState } from "react";
import { API_BASE_URL } from "../../../config";
import "../../../styles/Schedule.css";

// When this screen is on air.
//
// --- the number this panel exists to show -------------------------------
//
// A daily rate means a full 24 hours, so an hour costs a twenty-fourth of it.
// That means a screen on air 8am-8pm can take at most HALF its daily rate in a
// day, however busy it is. Obvious once stated and a nasty surprise if it is
// not, so `max_daily_cents` is shown right next to the hours while they are
// being chosen -- not on a confirmation screen afterwards.
//
// --- what saving does not do -------------------------------------------
//
// Narrowing your hours does not cancel bookings that fall outside them. That
// is a commercial decision -- a refund, or a conversation -- not something a
// settings form should execute silently. The server returns the affected
// bookings and this shows them; the takedown lever is elsewhere and stays a
// deliberate act.

const DAYS = [
    { index: 0, short: "Mon", long: "Monday" },
    { index: 1, short: "Tue", long: "Tuesday" },
    { index: 2, short: "Wed", long: "Wednesday" },
    { index: 3, short: "Thu", long: "Thursday" },
    { index: 4, short: "Fri", long: "Friday" },
    { index: 5, short: "Sat", long: "Saturday" },
    { index: 6, short: "Sun", long: "Sunday" },
];

// Minutes from local midnight. 1440 is "end of day" and renders as 12am --
// nobody writes "24:00" on a shop door.
function toTimeValue(minutes) {
    const m = Math.min(minutes, 1439);
    return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

function fromTimeValue(value, isClose) {
    const [h, m] = (value || "0:0").split(":").map(Number);
    const minutes = h * 60 + m;
    // A close of 00:00 means midnight at the END of the day, which is 1440.
    // Without this, "8am to midnight" saves as a zero-length window.
    if (isClose && minutes === 0) return 1440;
    return minutes;
}

function money(cents) {
    if (cents === null || cents === undefined) return "—";
    return `$${(cents / 100).toFixed(2)}`;
}

export default function ScheduleSettings({ deviceId }) {
    const [data, setData] = useState(null);
    const [mode, setMode] = useState("always");
    const [rows, setRows] = useState({});      // weekday -> [{open, close}]
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState("");
    const [error, setError] = useState("");
    const [outside, setOutside] = useState([]);

    const token = localStorage.getItem("token");

    const hydrate = useCallback((payload) => {
        setData(payload);
        setMode(payload.schedule_mode || "always");
        const next = {};
        (payload.windows || []).forEach((w) => {
            (next[w.weekday] = next[w.weekday] || []).push({
                open: w.open_minute,
                close: w.close_minute,
            });
        });
        setRows(next);
    }, []);

    const load = useCallback(async () => {
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/vendor/devices/${deviceId}/schedule`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) return;
            hydrate(await res.json());
        } catch {
            /* the panel simply does not appear; the rest of the page is fine */
        }
    }, [deviceId, token, hydrate]);

    useEffect(() => { load(); }, [load]);

    function applyPreset(which) {
        if (which === "always") {
            setMode("always");
            return;
        }
        setMode("weekly");
        const next = {};
        DAYS.forEach((d) => { next[d.index] = [{ open: 8 * 60, close: 20 * 60 }]; });
        setRows(next);
    }

    function setDayOpen(weekday, open) {
        setRows((r) => ({
            ...r,
            [weekday]: open ? (r[weekday]?.length ? r[weekday] : [{ open: 8 * 60, close: 20 * 60 }]) : [],
        }));
    }

    function updateWindow(weekday, i, field, value) {
        setRows((r) => {
            const list = [...(r[weekday] || [])];
            list[i] = { ...list[i], [field]: value };
            return { ...r, [weekday]: list };
        });
    }

    function addWindow(weekday) {
        setRows((r) => ({
            ...r,
            [weekday]: [...(r[weekday] || []), { open: 14 * 60, close: 18 * 60 }],
        }));
    }

    function removeWindow(weekday, i) {
        setRows((r) => ({
            ...r,
            [weekday]: (r[weekday] || []).filter((_, j) => j !== i),
        }));
    }

    async function save() {
        setBusy(true);
        setError("");
        setNote("");
        setOutside([]);
        const windows = [];
        Object.entries(rows).forEach(([weekday, list]) => {
            (list || []).forEach((w) => {
                windows.push({
                    weekday: Number(weekday),
                    open_minute: w.open,
                    close_minute: w.close,
                });
            });
        });
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/vendor/devices/${deviceId}/schedule`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ schedule_mode: mode, windows }),
                }
            );
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(body.message || "That did not save.");
                return;
            }
            setNote(body.message || "Saved.");
            if (body.bookings_outside_hours) setOutside(body.bookings_outside_hours);
            if (body.warning) setError(body.warning);
            hydrate({ ...data, ...body });
        } catch {
            setError("Network error. Nothing was saved.");
        } finally {
            setBusy(false);
        }
    }

    if (!data) return null;

    const weekly = mode === "weekly";
    // Recomputed locally so the figure tracks the form rather than the last
    // save -- the whole point is seeing it while you choose.
    const openMinutes = weekly
        ? Object.values(rows).flat().reduce((t, w) => t + (w.close - w.open), 0)
        : 7 * 24 * 60;
    const maxDaily =
        data.daily_rate
            ? Math.round((data.daily_rate * 100 / 24) * (openMinutes / (60 * 7)))
            : null;

    return (
        <section className="sch">
            <div className="sch__head">
                <h3 className="sch__title">On-air hours</h3>
                <p className="sch__sub">
                    Times are local to the screen ({data.timezone}). Advertisers can
                    only book hours you are on air.
                </p>
            </div>

            <div className="sch__presets" role="group" aria-label="Schedule presets">
                <button
                    type="button"
                    className={"sch__preset" + (!weekly ? " is-active" : "")}
                    onClick={() => applyPreset("always")}
                >
                    24/7
                </button>
                <button
                    type="button"
                    className={"sch__preset" + (weekly ? " is-active" : "")}
                    onClick={() => applyPreset("business")}
                >
                    Set my hours
                </button>
            </div>

            {weekly && (
                <ul className="sch__days">
                    {DAYS.map((day) => {
                        const list = rows[day.index] || [];
                        const open = list.length > 0;
                        return (
                            <li className="sch__day" key={day.index}>
                                <label className="sch__day-toggle">
                                    <input
                                        type="checkbox"
                                        checked={open}
                                        onChange={(e) => setDayOpen(day.index, e.target.checked)}
                                    />
                                    <span className="sch__day-name">{day.short}</span>
                                </label>

                                {open ? (
                                    <div className="sch__windows">
                                        {list.map((w, i) => (
                                            <div className="sch__window" key={i}>
                                                <input
                                                    type="time"
                                                    className="wb-input sch__time"
                                                    value={toTimeValue(w.open)}
                                                    onChange={(e) =>
                                                        updateWindow(day.index, i, "open",
                                                            fromTimeValue(e.target.value, false))}
                                                    aria-label={`${day.long} opens`}
                                                />
                                                <span className="sch__dash">to</span>
                                                <input
                                                    type="time"
                                                    className="wb-input sch__time"
                                                    value={toTimeValue(w.close)}
                                                    onChange={(e) =>
                                                        updateWindow(day.index, i, "close",
                                                            fromTimeValue(e.target.value, true))}
                                                    aria-label={`${day.long} closes`}
                                                />
                                                {list.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="sch__x"
                                                        onClick={() => removeWindow(day.index, i)}
                                                        aria-label="Remove this window"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        {/* A second window is how a lunchtime closure is
                                            expressed. Hours that cross midnight are also
                                            two windows -- 10pm to midnight, then midnight
                                            to 2am on the next day. */}
                                        <button
                                            type="button"
                                            className="sch__add"
                                            onClick={() => addWindow(day.index)}
                                        >
                                            + another window
                                        </button>
                                    </div>
                                ) : (
                                    <span className="sch__closed">Closed</span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            <div className="sch__summary">
                <div>
                    <span className="sch__k">On air</span>
                    <span className="sch__v">
                        {weekly ? `${(openMinutes / 60).toFixed(0)} hours a week` : "Always"}
                    </span>
                </div>
                <div>
                    <span className="sch__k">Per hour</span>
                    <span className="sch__v">{data.hourly_rate_display || "—"}</span>
                </div>
                <div>
                    <span className="sch__k">Most you can earn in a day</span>
                    <span className="sch__v">{money(maxDaily)}</span>
                    {/* Said plainly, because the arithmetic is not obvious and
                        discovering it from a payout statement is worse. */}
                    <span className="sch__note">
                        Your rate is for a full 24 hours, so an hour costs a
                        twenty-fourth of it.
                    </span>
                </div>
            </div>

            {error && <p className="sch__error">{error}</p>}
            {note && !error && <p className="sch__ok">{note}</p>}

            {outside.length > 0 && (
                <div className="sch__outside">
                    <strong>These bookings now fall partly outside your hours.</strong>
                    <p>
                        They are untouched and still paid for — the screen just will not
                        play them while it is off air. Take them down from the requests
                        list if that is not what you want.
                    </p>
                    <ul>
                        {outside.map((b) => (
                            <li key={b.request_id}>
                                Booking #{b.request_id} — {b.hours_off_air} of{" "}
                                {b.hours_total} hours off air
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="sch__actions">
                <button
                    type="button"
                    className="wb-btn wb-btn--primary"
                    onClick={save}
                    disabled={busy}
                >
                    {busy ? "Saving…" : "Save hours"}
                </button>
                <span className="sch__hint">
                    The screen picks this up at its next check-in, within about a minute.
                </span>
            </div>
        </section>
    );
}
